/**
 * EvidenceTabs.tsx — URL / Text / File evidence capture for Answer Lane
 *
 * W21: Primary Answer Surface
 *
 * - URL tab: https:// validation (server rule EVIDENCE_URL_NOT_HTTPS)
 * - Text tab: min 20 chars for evidence to count (server rule)
 * - File tab: file upload (stores file URL in evidence_file_url)
 * - is_assumption is computed server-side — no checkbox here
 */

import { useState } from "react";

export interface EvidenceValue {
  evidence_url?: string;
  evidence_text?: string;
  evidence_file_url?: string;
  active_tab?: "url" | "text" | "file";
}

interface Props {
  value: EvidenceValue;
  onChange: (v: EvidenceValue) => void;
  defaultOpen?: boolean;
}

type Tab = "url" | "text" | "file";

export default function EvidenceTabs({ value, onChange, defaultOpen = false }: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const [tab, setTab] = useState<Tab>((value.active_tab as Tab) ?? "url");
  const [urlError, setUrlError] = useState<string | null>(null);
  const [textWarn, setTextWarn] = useState(false);

  function handleTabChange(t: Tab) {
    setTab(t);
    onChange({ ...value, active_tab: t });
  }

  function handleUrlChange(raw: string) {
    setUrlError(null);
    onChange({ ...value, evidence_url: raw, active_tab: tab });
    if (raw && !raw.startsWith("https://")) {
      setUrlError("URL must start with https://");
    }
  }

  function handleTextChange(text: string) {
    onChange({ ...value, evidence_text: text, active_tab: tab });
    setTextWarn(text.length > 0 && text.length < 20);
  }

  const hasEvidence =
    (value.evidence_url && value.evidence_url.startsWith("https://")) ||
    (value.evidence_text && value.evidence_text.length >= 20) ||
    value.evidence_file_url;

  return (
    <div
      style={{
        border: "1px solid #E5E5E7",
        borderRadius: 8,
        overflow: "hidden",
        marginTop: 12,
      }}
    >
      {/* Header toggle */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        style={{
          width: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "9px 12px",
          background: open ? "#F9FAFB" : "#FAFAF7",
          border: "none",
          cursor: "pointer",
          textAlign: "left",
          borderBottom: open ? "1px solid #E5E5E7" : "none",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
          <span
            style={{
              fontSize: 10,
              fontWeight: 700,
              textTransform: "uppercase",
              letterSpacing: "0.14em",
              color: hasEvidence ? "#16A34A" : "#6B6B70",
            }}
          >
            Evidence
          </span>
          {hasEvidence && (
            <span
              style={{
                fontSize: 10,
                color: "#16A34A",
                fontWeight: 600,
              }}
            >
              Linked
            </span>
          )}
        </div>
        <span style={{ fontSize: 12, color: "#9CA3AF" }}>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div>
          {/* Tab strip */}
          <div
            style={{
              display: "flex",
              borderBottom: "1px solid #E5E5E7",
              background: "#FAFAF7",
            }}
          >
            {(["url", "text", "file"] as Tab[]).map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => handleTabChange(t)}
                style={{
                  padding: "7px 14px",
                  fontSize: 12,
                  fontWeight: tab === t ? 700 : 500,
                  color: tab === t ? "#0B0B0F" : "#6B6B70",
                  background: "transparent",
                  border: "none",
                  borderBottom: tab === t ? "2px solid #0B0B0F" : "2px solid transparent",
                  cursor: "pointer",
                  textTransform: "capitalize",
                  transition: "all 0.1s",
                }}
                aria-selected={tab === t}
              >
                {t === "url" ? "URL" : t === "text" ? "Text" : "File"}
              </button>
            ))}
          </div>

          {/* Tab content */}
          <div style={{ padding: 12, background: "#FFFFFF" }}>
            {tab === "url" && (
              <div>
                <input
                  type="url"
                  placeholder="https://..."
                  value={value.evidence_url ?? ""}
                  onChange={(e) => handleUrlChange(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    border: `1px solid ${urlError ? "#DC2626" : "#C9C9CC"}`,
                    borderRadius: 6,
                    fontSize: 13,
                    color: "#0B0B0F",
                    outline: "none",
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                  }}
                  aria-label="Evidence URL"
                  aria-invalid={!!urlError}
                />
                {urlError && (
                  <p style={{ fontSize: 11, color: "#DC2626", marginTop: 4 }}>{urlError}</p>
                )}
                <p style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>
                  Paste a link to a document, email thread, or web page that supports this answer.
                </p>
              </div>
            )}

            {tab === "text" && (
              <div>
                <textarea
                  rows={3}
                  placeholder="Paste a quote, meeting note, or email excerpt — min 20 characters to count as evidence."
                  value={value.evidence_text ?? ""}
                  onChange={(e) => handleTextChange(e.target.value)}
                  style={{
                    width: "100%",
                    padding: "8px 10px",
                    border: "1px solid #C9C9CC",
                    borderRadius: 6,
                    fontSize: 13,
                    color: "#0B0B0F",
                    outline: "none",
                    boxSizing: "border-box",
                    resize: "vertical",
                    fontFamily: "inherit",
                    lineHeight: 1.5,
                  }}
                  aria-label="Evidence text"
                />
                {textWarn && (
                  <p style={{ fontSize: 11, color: "#EA580C", marginTop: 4 }}>
                    At least 20 characters required to count as verified evidence.
                  </p>
                )}
                <p style={{ fontSize: 11, color: "#9CA3AF", marginTop: 4 }}>
                  {(value.evidence_text ?? "").length} characters
                </p>
              </div>
            )}

            {tab === "file" && (
              <div>
                {value.evidence_file_url ? (
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "8px 10px",
                      background: "#F4F4F5",
                      borderRadius: 6,
                      border: "1px solid #E5E5E7",
                    }}
                  >
                    <span style={{ fontSize: 12, color: "#0B0B0F", flex: 1, wordBreak: "break-all" }}>
                      {value.evidence_file_url}
                    </span>
                    <button
                      type="button"
                      onClick={() => onChange({ ...value, evidence_file_url: undefined })}
                      style={{
                        fontSize: 11,
                        color: "#DC2626",
                        background: "transparent",
                        border: "none",
                        cursor: "pointer",
                        fontWeight: 600,
                        flexShrink: 0,
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ) : (
                  <div>
                    <input
                      type="file"
                      accept=".pdf,.docx,.xlsx,.png,.jpg,.jpeg,.txt"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          // Store file name as placeholder — server handles actual upload
                          onChange({
                            ...value,
                            evidence_file_url: `[file:${file.name}]`,
                            active_tab: "file",
                          });
                        }
                      }}
                      style={{
                        fontSize: 13,
                        color: "#525258",
                        width: "100%",
                      }}
                      aria-label="Upload evidence file"
                    />
                    <p style={{ fontSize: 11, color: "#9CA3AF", marginTop: 6 }}>
                      Accepted: PDF, DOCX, XLSX, PNG, JPG, TXT
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
